import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Heading,
  Text,
  Link,
  useColorModeValue,
  Alert,
  AlertIcon,
  Container,
  Flex,
  Icon,
  Badge,
  VStack,
  HStack,
  Divider,
  Image,
} from '@chakra-ui/react';
import { MdAdminPanelSettings, MdSecurity, MdBusiness } from 'react-icons/md';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../services/firebase';

export default function SignIn() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const bgGradient = useColorModeValue(
    'linear(to-br, blue.50, purple.50, pink.50)',
    'linear(to-br, gray.900, blue.900, purple.900)'
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const accentColor = useColorModeValue('blue.600', 'blue.400');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      
      // Check if user needs to reset password (first login or expired)
      const user = auth.currentUser;
      if (user) {
        const idTokenResult = await user.getIdTokenResult();
        const firstLogin = idTokenResult.claims.firstLogin === true;
        const passwordExpiresAt = idTokenResult.claims.passwordExpiresAt as string | undefined;
        
        // Check if password expired
        let isExpired = false;
        if (passwordExpiresAt) {
          const expiryDate = new Date(passwordExpiresAt);
          isExpired = expiryDate < new Date();
        }
        
        // Redirect to password reset if first login or expired
        if (firstLogin || isExpired) {
          navigate('/auth/reset-password');
          return;
        }
      }
      
      navigate('/admin/dashboard');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bgGradient={bgGradient}
      position="relative"
      overflow="hidden"
    >
      {/* Background decoration */}
      <Box
        position="absolute"
        top="-10%"
        right="-5%"
        width="500px"
        height="500px"
        borderRadius="full"
        bg={useColorModeValue('blue.100', 'blue.900')}
        opacity="0.3"
        filter="blur(100px)"
      />
      <Box
        position="absolute"
        bottom="-10%"
        left="-5%"
        width="500px"
        height="500px"
        borderRadius="full"
        bg={useColorModeValue('purple.100', 'purple.900')}
        opacity="0.3"
        filter="blur(100px)"
      />

      <Container maxW="md" position="relative" zIndex={1}>
        <VStack spacing={8}>
          {/* Logo */}
          <Image src="/dossiloc-logo1.png" alt="DossiLoc" h="100px" mx="auto" />
          
          {/* Admin Badge */}
          <HStack spacing={3}>
            <Icon as={MdAdminPanelSettings} w={10} h={10} color={accentColor} />
            <VStack spacing={0} align="start">
              <Heading size="xl" color={accentColor}>
                Admin Portal
              </Heading>
            </VStack>
          </HStack>

          {/* Sign In Card */}
          <Box
            w="full"
            bg={cardBg}
            p={8}
            borderRadius="2xl"
            boxShadow="2xl"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Stack spacing={6}>
              {/* Header */}
              <VStack spacing={2}>
                <Badge
                  colorScheme="blue"
                  fontSize="sm"
                  px={3}
                  py={1}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <Icon as={MdSecurity} />
                  Administrator Access
                </Badge>
                <Heading size="lg" textAlign="center">
                  Sign In
                </Heading>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  Access restricted to authorized personnel only
                </Text>
              </VStack>

              <Divider />

              {error && (
                <Alert status="error" borderRadius="lg">
                  <AlertIcon />
                  {error}
                </Alert>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <Stack spacing={5}>
                  <FormControl isRequired>
                    <FormLabel fontWeight="semibold">Email Address</FormLabel>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@dossiloc.ca"
                      size="lg"
                      borderRadius="lg"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontWeight="semibold">Password</FormLabel>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      size="lg"
                      borderRadius="lg"
                    />
                  </FormControl>

                  <Button
                    type="submit"
                    colorScheme="blue"
                    size="lg"
                    isLoading={loading}
                    loadingText="Authenticating..."
                    borderRadius="lg"
                    leftIcon={<Icon as={MdAdminPanelSettings} />}
                  >
                    Access Admin Portal
                  </Button>
                </Stack>
              </form>

              <Divider />

              {/* Footer */}
              <VStack spacing={3}>
                <Text textAlign="center" fontSize="sm">
                  <Link as={RouterLink} to="/auth/forgot-password" color="blue.500" fontWeight="semibold">
                    Forgot password?
                  </Link>
                </Text>
                
                <HStack spacing={2} fontSize="xs" color="gray.500">
                  <Icon as={MdBusiness} />
                  <Text>
                    Internal Use Only • Authorized Access Required
                  </Text>
                </HStack>
              </VStack>
            </Stack>
          </Box>

          {/* Support Text */}
          <Text fontSize="xs" color="gray.500" textAlign="center" maxW="sm">
            This is a restricted admin portal. For support or access requests, contact{' '}
            <Link href="mailto:admin@dossiloc.ca" color="blue.500" fontWeight="semibold">
              admin@dossiloc.ca
            </Link>
          </Text>
        </VStack>
      </Container>
    </Flex>
  );
}
